import { valibotResolver } from "@hookform/resolvers/valibot";
import { useForm } from "react-hook-form";

import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from "common/components/ui/Dialog";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";

import useApiTokensMutations from "../../datahooks/useApiTokensMutations";
import { ApiKeyNameSchema } from "../../schema";

import type { ApiToken } from "../../types";

interface EditNameDialogProps {
  apiToken: ApiToken;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}

export default function EditNameDialog({
  apiToken,
  isOpen,
  onOpenChange,
}: EditNameDialogProps) {
  const { name, id: tokenId } = apiToken || {};

  const {
    register,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<{
    name: string;
  }>({
    resolver: valibotResolver(ApiKeyNameSchema),
    defaultValues: { name },
  });

  const { updateApiToken, isUpdatingToken } = useApiTokensMutations();

  function onSubmit(data: { name: string }): void {
    updateApiToken({
      tokenId,
      updates: { name: data.name },
    }).then((newToken) => {
      reset({ name: newToken.name });
      onOpenChange(false);
    });
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        {/* Title */}
        <DialogTitle className="mb-8">Edit API Key</DialogTitle>

        {/* Form input for editing key name */}
        <form
          className="flex flex-col gap-y-8"
          onSubmit={handleSubmit(onSubmit)}
        >
          <FormField
            label="Key name"
            htmlFor="name"
            error={errors.name?.message}
          >
            <Input {...register("name")} defaultValue={name} id="name" />
          </FormField>
          {/* Action buttons */}
          <div className="flex justify-between gap-x-2.5">
            <DialogClose asChild>
              <Button
                className="lg:flex-1"
                disabled={isUpdatingToken}
                variant="secondary-black"
              >
                Cancel
              </Button>
            </DialogClose>

            <Button
              className="lg:flex-1"
              isLoading={isUpdatingToken}
              type="submit"
            >
              Save
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
